export enum ServiceStatus {
  IDLE = "idle",
  LOADING = "loading",
  SUCCESS = "success",
  ERROR = "error",
  API_ERROR = "api_error",
}

export enum ServiceStep {
  GUIDE = 0,
  UPLOAD = 1,
  FACE_BLUR = 2,
  HEIGHT_INPUT = 3,
  PROCESSING = 4,
  RESULTS = 5,
}

export interface BaseServiceState {
  currentStep: ServiceStep;
  status: ServiceStatus;
  error: string | null;
  canProceedToNextStep: boolean;
}

export interface BaseServiceActions {
  setCurrentStep: (step: ServiceStep) => void;
  setStatus: (status: ServiceStatus) => void;
  setError: (error: string | null) => void;
  setCanProceedToNextStep: (value: boolean) => void;
  resetService: () => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
}

export interface BaseServiceInterface extends BaseServiceState, BaseServiceActions {}

export interface ServiceApiResponse<T = any> {
  ok: boolean;
  message: string;
  data?: T;
  status_code: number;
}

export interface GuideSlide {
  id: string;
  title: string;
  description: string;
  image?: string;
  list?: string[];
}

export interface ServiceConfig {
  serviceId: string;
  serviceName: string;

  guide: {
    slides: GuideSlide[];
    showPagination: boolean;
  };

  configuration?: {
    requiresHeight?: boolean;
    heightUnits?: ("cm" | "inch")[];
    heightValidation?: {
      minCm: number;
      maxCm: number;
    };
    customFields?: any;
  };

  upload?: {
    photoCount: 1 | 2 | "multiple";
    maxImages?: number;
    photoLabels?: string[];
    photoDescriptions?: {
      title: string;
      instructions: string[];
    }[];
    placeholderImages?: string[];
  };

  camera?: {
    enabled?: boolean;
    photoCount?: 1 | 2;
    requiresOrientation?: boolean;
    requiresMicrophone?: boolean;
    voiceGuidance?: boolean;
  };

  processing: {
    requiresFaceBlur?: boolean;
    imageValidation: {
      maxSizeKB: number;
      allowedTypes: string[];
      minWidth?: number;
      minHeight?: number;
    };
  };

  api: {
    endpoint: string;
    method: "POST";
    requiresAuth: boolean;
  };

  results: {
    template: "body-measurement" | "auto-tagging" | "visual-search" | "garment-measurement";
    allowExport?: boolean;
    allowSave?: boolean;
    unitConversion?: boolean;
  };
}

export interface ServiceError {
  type: "NETWORK" | "VALIDATION" | "API" | "PROCESSING" | "PERMISSION";
  message: string;
  code?: string;
  details?: any;
}

export interface CameraPermissions {
  camera: boolean;
  microphone?: boolean;
  orientation?: boolean;
}

export interface ImageUpload {
  file: File | null;
  base64: string | null;
  preview: string | null;
  isValid: boolean;
  error?: string;
  blurredBase64?: string | null;
}

export type MeasurementUnit = "cm" | "inch";

export interface FaceBlurRequest {
  image: string;
}

export interface FaceBlurResponse {
  ok: boolean;
  message: string;
  data?: {
    blurred_image: string;
    face_detected: boolean;
  };
  status_code: number;
}

export interface FaceBlurResult {
  success: boolean;
  blurred_image?: string;
  error?: string;
}

export interface BodyMeasurementRequest {
  present_height: string;
  img_full_view_body: string;
  img_side_view_body: string;
  ref_id?: string;
}

export interface MeasurementPoint {
  X: number;
  Y: number;
}

export interface MeasurementLocation {
  X1: number;
  Y1: number;
  X2: number;
  Y2: number;
}

export interface MeasurementPoints {
  FullViewImage: {
    Point1: MeasurementPoint;
    Point2: MeasurementPoint;
  };
  SideViewImage: {
    Point1: MeasurementPoint;
    Point2: MeasurementPoint;
  };
}

export interface MeasurementDetail {
  Size: number;
  Location: MeasurementLocation;
  Points: MeasurementPoints;
}

export interface BodyMeasurementResult {
  Gender: "Male" | "Female" | "Undefined";
  BodyType: "Plus-size" | "Normal";
  Shoulder: number;
  Chest: number;
  Waist: number;
  Hips: number;
  Neck: number;
  UnderChest: number;
  Wrist: number;
  Knee: number;
  UpperBody: number;
  LowerBody: number;
  ArmLength: number;
  Details: {
    Shoulder: MeasurementDetail;
    Chest: MeasurementDetail;
    Waist: MeasurementDetail;
    Hips: MeasurementDetail;
    Neck: MeasurementDetail;
    UnderChest: MeasurementDetail;
    Wrist: MeasurementDetail;
    Knee: MeasurementDetail;
    UpperBody: MeasurementDetail;
    LowerBody: MeasurementDetail;
    ArmLength: MeasurementDetail;
  };
  FullViewImage: string;
  SideViewImage: string;
  MetaData: {
    Date: string;
    FileNames: string[];
  };
}

export interface BodyMeasurementApiResponse {
  ok: boolean;
  message: string;
  data?: {
    result: BodyMeasurementResult;
  };
  status_code: number;
}

export interface MeasurementResultData {
  title: string;
  value: number;
  type: string;
  imageType: "FRONT" | "BACK";
  icon?: React.ReactNode;
  activeIcon?: React.ReactNode;
}

export interface SizeGuideRequest {
  KeyList: string[];
  Size: {
    Shoulder: number;
    Chest: number;
    Waist: number;
    Hips: number;
    Neck: number;
    UnderChest: number;
    Wrist: number;
    Knee: number;
    UpperBody: number;
    LowerBody: number;
    ArmLength: number;
    FullViewImage: string;
    SideViewImage: string;
  };
}

export interface SizeGuideResponse extends ServiceApiResponse<any[]> {}

export interface AvailableChartsResponse extends ServiceApiResponse<any> {}

export interface AutoTaggingRequest {
  image: string;
  ref_id?: string;
}

export interface ClothingItem {
  Gender: string;
  ClotheType: string;
  Design: string;
  Sleeve: string;
  Neckline: string;
  Region: {
    X1: number;
    Y1: number;
    X2: number;
    Y2: number;
  };
  Colors: string[];
  DressLength?: string;
}

export interface AutoTaggingResult {
  Clothes: ClothingItem[];
  MetaData: {
    Date: string;
    FileNames: string[];
  };
}

export interface AutoTaggingResponse extends ServiceApiResponse<{ result: AutoTaggingResult }> {}

export interface VisualSearchClothesRecognitionRequest {
  image: string;
  ref_id?: string;
}

export interface VisualSearchClothesRecognitionResponse {
  ok: boolean;
  message: string;
  data?: {
    result: AutoTaggingResult;
  };
  status_code: number;
}

export interface SearchResultItem {
  Brand: string;
  CurrentPrice: string;
  ImgUrl: string[];
  Name: string;
  PreviousPrice: string | null;
  Url: string;
}

export interface VisualSearchRequest {
  Gender: string;
  ClotheType: string;
  Design?: string;
  Sleeve?: string;
  Neckline?: string;
  Region: {
    X1: number;
    Y1: number;
    X2: number;
    Y2: number;
  };
  Colors?: string[];
  DressLength?: string;
  Country: string;
  Count: number;
  Page: number;
}

export interface VisualSearchResult {
  Result: SearchResultItem[];
  count_result: number;
  current_page: number;
  last_page: number;
  total: number;
}

export interface VisualSearchResponse {
  ok: boolean;
  message: string;
  data?: VisualSearchResult;
  status_code: number;
}

export interface ClothingItemSearchState {
  loading: boolean;
  error: string | null;
  results: SearchResultItem[];
  pagination: {
    currentPage: number;
    totalPages: number;
  };
}

export interface VisualSearchResultsMap {
  [itemIndex: number]: ClothingItemSearchState;
}

export interface GarmentMeasurementRequest {
  image: string;
  ref_id?: string;
}

export interface GarmentMeasurementPoint {
  X: number;
  Y: number;
}

export interface GarmentMeasurement {
  Size: number;
  Points: {
    Point1: GarmentMeasurementPoint;
    Point2: GarmentMeasurementPoint;
  };
}

export interface GarmentMeasurementResult {
  Measurements: {
    [key: string]: GarmentMeasurement;
  };
  MetaData: {
    Date: string;
    FileNames: string[];
  };
}

export interface GarmentMeasurementResponse extends ServiceApiResponse<GarmentMeasurementResult> {}
